import { AdFormat, WBEAdFormatOption } from "../api/contracts/site-config";

/**
 * Which format the storefront shows, given what the guest picked, what the URL
 * asked for, and what the site actually offers.
 *
 * The site's own module is the default, but it is not always on offer: the
 * storefront drops a format with nothing to sell, so a site whose rooms are
 * all `package_only` offers packages and no lodging. Defaulting to lodging
 * there lands the guest on a grid that is empty by construction, and the
 * switcher hides itself at one option, so nothing would get them out of it.
 */
export const resolveAdFormat = (
  selected: AdFormat | null,
  urlFormat: AdFormat | null,
  siteModule: AdFormat | undefined,
  options: WBEAdFormatOption[],
): AdFormat | undefined => {
  // No options at all means the site never configured the switcher, and the
  // WBE behaves exactly as it did before formats were selectable.
  if (options.length === 0) return selected ?? urlFormat ?? siteModule;

  const offers = (format: AdFormat | null | undefined): format is AdFormat =>
    format != null && options.some((option) => option.module === format);

  // The guest's own pick wins, but only while the site still offers it. The
  // configuration refetches periodically, so a format can be withdrawn under
  // them — packages switched off, its last ad expired — and keeping the stale
  // pick would strand the guest on a page the switcher no longer has a tab for.
  if (offers(selected)) return selected;
  if (offers(urlFormat)) return urlFormat;
  if (offers(siteModule)) return siteModule;

  // The site's own module has nothing to sell. Prefer its own packages, then
  // its own offers, over whatever comes first: this branch exists for the site
  // whose rooms are all `package_only`, and options arrive in the operator's
  // tab order, so falling through to `options[0]` could land exactly that site
  // on a sibling's "activity" just because it was arranged first.
  const ownFormat = LODGING_FORMAT_PREFERENCE.map((module) =>
    options.find((option) => option.module === module),
  ).find((option) => option != null);
  return (ownFormat ?? options[0]).module;
};

const LODGING_FORMAT_PREFERENCE: AdFormat[] = ["package", "offer"];

/**
 * The formats the storefront may show, from what the site config offers.
 * Offers have a grid only while offer ads are on; otherwise the tab would
 * open a page that is empty by construction.
 */
export const landingAdFormats = (
  options: WBEAdFormatOption[],
  featureFlags: readonly string[],
): WBEAdFormatOption[] => {
  const showsOffers = featureFlags.includes("wbe-offer-ads");
  if (showsOffers || !options.some((option) => option.module === "offer"))
    return options;
  return options.filter((option) => option.module !== "offer");
};
